// Scheduler — kanban-card scheduled-task route + shared result processor (ADR-0011 PR-6b).
//
// route/fire the scheduled task as a kanban card, reconcile + deliver its
// result, and processClaudeTaskResult (the shared result handler invoked here
// for the card path and by execution.ts for the one-shot path — kept here so
// the execution → kanban-route edge stays one-way).

import { createHash } from 'node:crypto';
import { buildScheduledTaskContextBlocks, classifyOutput, parseDeliverAssertion, isVacuousDeliverReason, SUPPRESS_SENTINEL, formatRunMarker } from '@augmented/core';
import { api, ApiError } from '../../api-client.js';
import { type SchedulerTaskState, loadSchedulerState, markTaskFired } from '../../claude-scheduler.js';
import { log } from '../runtime.js';
import { injectMessageWithStatus } from '../../persistent-session.js';
import {
  isPlainScheduledTemplate,
  isManagerSideEffectTemplate,
  parseStandupSummary,
  parsePlanItems,
  parseKanbanUpdates,
  STANDUP_TEMPLATES,
  TASK_UPDATE_TEMPLATES,
  PLAN_TEMPLATES,
  KANBAN_WORK_TEMPLATES,
  type BoardItem,
} from '../kanban/parsers.js';
import { reportDeliveryStatus } from '../delivery/notifications.js';
import { deliverScheduledTaskOutput } from '../delivery/scheduled-output.js';
import { deriveScheduledTaskNotify } from './tasks.js';
import { startRun, finishRun, fetchPriorScheduledRuns } from './runs.js';
import {
  trackScheduledRun,
  claimScheduledCardDelivery,
  releaseScheduledCardDelivery,
  markScheduledCardDeliveryComplete,
  scheduledRunsByCode,
  claudeSchedulerStates,
  completedScheduledCards,
  type ScheduledDeliveryResult,
} from './state.js';


/** Full, untruncated delivery fields for one card (from POST /host/kanban-item). */
export interface ScheduledCardForDelivery {
  id: string;
  title: string;
  status: string;
  result: string | null;
  deliverable: string | null;
  source_type: string | null;
  source_ref: string | null;
  last_actor_id: string | null;
  completed_at: string | null;
  /** ENG-6094: structured opt-out set via kanban_done — honor before the
   *  sentinel classifier. Optional: an older API that doesn't return the
   *  column yet simply never suppresses via this path. */
  suppress_delivery?: boolean | null;
  /** ENG-6839: the concrete trigger the agent named when asserting delivery
   *  (suppress_delivery: false) on a conditional card. A vacuous reason is
   *  rejected and the run suppressed - the kanban mirror of the oneshot
   *  `<deliver: REASON>` marker. Tri-state across the rollout window (see the
   *  gate's deploy-skew guard): `undefined` = an older API did not return this
   *  column, so the gate cannot tell whether a reason was named and falls back
   *  to delivering an explicit assert (LENIENT, not vacuous); `null` = a new API
   *  where the agent named no reason, which IS rejected as vacuous; a non-empty
   *  string is checked by isVacuousDeliverReason. */
  delivery_reason?: string | null;
}

// ENG-5601: manager-side delivery from a done scheduled-task card. Centralises
// delivery + suppression manager-side (the locked 2026-05-26 decision): the
// in-session agent records its result on the card and marks it done WITHOUT
// sending anything itself; the manager then reads the full result and delivers
// via the existing processClaudeTaskResult path (deliveryMode/channel/to +
// `<no-delivery/>` handling).
//
// Called from two places against a shared idempotency claim:
//   - reconcileScheduledRuns (poll path) — the PRIMARY trigger, because the
//     agent self-completes the card and realtime onCompletion is therefore
//     suppressed by isSelfCompletion. Passes completedBy='self'.
//   - the realtime onCompletion handler — only for non-self (human) completions
//     of a scheduled-task card. Passes completedBy='user'.
//
// ENG-6546: `completedBy` distinguishes those two callers so the manager-side
// template side effect (standup / task-update → /host/agent-status) runs ONLY on
// the agent's own self-completion. A human closing such a card from the console
// must NOT have their free text parsed as standup/current_tasks and posted to the
// agent's status — that gate (preserved from ENG-5601) is now keyed on the actor
// rather than on the template being "plain".
//
// Returns a ScheduledDeliveryResult so the caller knows whether it may close the
// run. Only 'delivered'/'terminal' authorise closing it; 'retry' (transient
// fetch failure, claim released) and 'in_flight' (another path holds an
// unresolved claim) keep the run open so the retry handle survives.
export async function deliverScheduledCardResult(
  codeName: string,
  agentId: string,
  cardId: string,
  completedBy: 'self' | 'user' = 'self',
): Promise<ScheduledDeliveryResult> {
  if (!claimScheduledCardDelivery(cardId)) {
    // Someone else owns the claim. If their delivery already RESOLVED, this is
    // terminal (close the run); if it's still in flight, the caller must keep
    // the run open — closing it now would drop the retry handle should that
    // in-flight attempt fail and release the claim.
    return completedScheduledCards.has(cardId) ? 'terminal' : 'in_flight';
  }
  let card: ScheduledCardForDelivery | undefined;
  try {
    const resp = await api.post<{ item?: ScheduledCardForDelivery }>('/host/kanban-item', {
      agent_id: agentId,
      item_id: cardId,
    });
    card = resp.item;
  } catch (err) {
    // A 4xx is terminal: a 404 means the card was deleted between the board read
    // and this fetch (or a 400/403 — none retryable), so resolve it and let the
    // caller close the run rather than re-fetching a gone card every poll until
    // the stale-run reaper steps in. Only a 5xx / network/transport error is
    // transient: release the claim and signal retry so the run stays open.
    if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
      log(`[scheduled-kanban] delivery: card ${cardId} on '${codeName}' fetch returned ${err.status} (${err.message}) — treating as terminal, not retrying`);
      markScheduledCardDeliveryComplete(cardId);
      return 'terminal';
    }
    releaseScheduledCardDelivery(cardId);
    log(`[scheduled-kanban] delivery fetch failed for card ${cardId} on '${codeName}': ${(err as Error).message} — will retry`);
    return 'retry';
  }

  // Terminal non-deliverable cases (resolve + let the caller close the run): a
  // failed/cancelled card has no deliverable; a non-scheduled_task card isn't
  // ours to deliver.
  if (!card || card.status !== 'done' || card.source_type !== 'scheduled_task' || !card.source_ref) {
    markScheduledCardDeliveryComplete(cardId);
    return 'terminal';
  }
  const state = claudeSchedulerStates.get(codeName) ?? loadSchedulerState(codeName);
  const task = state.tasks[card.source_ref];
  if (!task) {
    log(`[scheduled-kanban] delivery: no scheduler task for source_ref=${card.source_ref} on '${codeName}' — skipping`);
    markScheduledCardDeliveryComplete(cardId);
    return 'terminal';
  }
  // ENG-5601 + ENG-6546: decide whether this card's result may be processed here.
  // Classes of template reach here:
  //   - plain (isPlainScheduledTemplate): pure delivery, no side effect — always OK.
  //   - morning-plan (PLAN_TEMPLATES): pure delivery FROM THE MANAGER's view. Its
  //     board mutation (creating plan cards) is now done IN-SESSION by the agent
  //     via kanban_add (ENG-6546 step 2), so the manager only delivers the card's
  //     summary text and never parses plan items — no non-idempotent INSERT here.
  //     Safe for both self and user completion.
  //   - manager-side-effect (standup / task-update): parses the result and POSTs to
  //     /host/agent-status. Allowed ONLY on the agent's self-completion
  //     (completedBy==='self'). A HUMAN closing such a card from the console
  //     (completedBy==='user') must NOT have their free text parsed as standup /
  //     current_tasks — skip, exactly as the old "plain only" guard did.
  //   - any other non-plain template (e.g. a legacy oneshot-materialised card, or
  //     kanban-work which never routes here): no in-session manager side effect
  //     defined — skip so we never parse free text into a side effect by accident.
  const isPlain = isPlainScheduledTemplate(task.templateId);
  const isPlanFamily = PLAN_TEMPLATES.has(task.templateId);
  const isManagerSideEffect = isManagerSideEffectTemplate(task.templateId);
  if (!isPlain && !isPlanFamily && !(isManagerSideEffect && completedBy === 'self')) {
    log(`[scheduled-kanban] delivery: card ${cardId} template '${task.templateId}' on '${codeName}' (completedBy=${completedBy}) has no eligible manager side effect — skipping manager delivery`);
    markScheduledCardDeliveryComplete(cardId);
    return 'terminal';
  }
  // ENG-6107: delivery_policy — decided at task-definition time, enforced
  // mechanically here. Four per-run agent-judgment iterations (PR #1829,
  // ENG-6084, ENG-6094 flag, strengthened wording) all failed live; the
  // policy removes the judgment. 'conditional' inverts the default: quiet
  // runs stay silent unless the agent explicitly ASSERTED delivery on
  // kanban_done (suppress_delivery: false). 'never' records on the card
  // only. Suppressions tombstone via last_delivery_status='skipped' +
  // SUPPRESSED_BY_POLICY so the schedule row shows why nothing arrived.
  const deliveryPolicy = task.deliveryPolicy ?? 'always';
  // ENG-6839: a conditional task delivers only when the agent ASSERTS via
  // kanban_done(suppress_delivery: false). Port the ENG-6803 oneshot hardening:
  // reject a vacuous delivery_reason so an assertion that names no concrete
  // trigger ("status check", "all clear", or none at all) is suppressed exactly
  // as if absent. That lets the kanban route stand alone as the
  // conditional gate once the claude -p oneshot is removed (ENG-6347): without
  // it an agent could rubber-stamp a send by asserting delivery with no real
  // reason. Only applies to an actual assertion; quiet runs (suppress_delivery
  // !== false) are already caught by the policy clause below.
  //
  // Deploy-skew guard: `delivery_reason === undefined` means the API did not
  // return the column (an older /host/kanban-item before this change), so we
  // CANNOT tell whether a reason was named: fall back to the pre-ENG-6839
  // behaviour (deliver on an explicit assert) rather than over-suppressing a
  // genuine delivery during the rollout window. A NEW API returns `null` when
  // the agent named no reason, which IS rejected.
  const vacuousAssertion =
    deliveryPolicy === 'conditional' &&
    card.suppress_delivery === false &&
    card.delivery_reason !== undefined &&
    isVacuousDeliverReason(card.delivery_reason);
  if (
    deliveryPolicy === 'never' ||
    (deliveryPolicy === 'conditional' && card.suppress_delivery !== false) ||
    vacuousAssertion
  ) {
    log(
      `[scheduled-kanban] Suppressed by delivery_policy=${deliveryPolicy}` +
        (vacuousAssertion ? ' (asserted delivery but reason was vacuous/empty)' : '') +
        ` for card ${cardId} (task '${task.name}') on '${codeName}' - result recorded on the card only (tombstone)`,
    );
    if (task.deliveryMode === 'announce' && task.deliveryTo) {
      await reportDeliveryStatus(agentId, task.taskId, {
        status: 'skipped',
        error_code: 'SUPPRESSED_BY_POLICY',
      });
    }
    markScheduledCardDeliveryComplete(cardId);
    return 'delivered';
  }
  // ENG-6094: structured opt-out wins before any content classification. The
  // agent declared "record but don't deliver" as a kanban_done parameter — the
  // reliable channel after two prose-contract iterations (PR #1829, ENG-6084)
  // failed live. Mirror the sentinel-suppress bookkeeping: report a skipped
  // announce delivery so the platform's delivery accounting stays consistent.
  if (card.suppress_delivery === true) {
    log(`[scheduled-kanban] Suppressing delivery (structured flag) for card ${cardId} (task '${task.name}') on '${codeName}' — result recorded on the card only`);
    if (task.deliveryMode === 'announce' && task.deliveryTo) {
      await reportDeliveryStatus(agentId, task.taskId, {
        status: 'skipped',
        error_code: 'NO_CONTENT',
      });
    }
    markScheduledCardDeliveryComplete(cardId);
    return 'delivered';
  }
  // ENG-6430: processClaudeTaskResult now reports whether the result actually
  // posted/delivered. On failure, release the claim and signal 'retry' so the
  // run-map (the only retry handle) survives and a later poll re-delivers,
  // instead of marking the card complete and silently dropping the result.
  //
  // Retry is clean on THIS path. Two cases reach here (gated above):
  //   - plain templates: no side effect, pure delivery — a re-poll just re-delivers.
  //   - manager-side-effect templates (standup / task-update, self-completion only,
  //     ENG-6546): their only /host mutation is `POST /host/agent-status`, an
  //     idempotent UPSERT of the agent's standup / current_tasks fields. A re-poll
  //     re-posts the SAME parsed status, overwriting with identical values — no
  //     double-apply.
  //   - morning-plan: the non-idempotent board INSERT is NOT done here — we pass
  //     skipBoardSideEffects so processClaudeTaskResult never parses plan items
  //     (the in-session agent already created the cards via kanban_add). So a
  //     re-poll cannot create duplicate cards.
  // So a failure here is safe to re-attempt, and the structured-outcome type can
  // stay coarse — every side effect that runs on this path is re-applyable.
  const outcome = await processClaudeTaskResult(codeName, agentId, task.templateId, card.result ?? '', {
    mode: task.deliveryMode,
    channel: task.deliveryChannel,
    to: task.deliveryTo,
    taskId: task.taskId,
    skipBoardSideEffects: true,
  });
  if (!outcome.ok) {
    releaseScheduledCardDelivery(cardId);
    log(`[scheduled-kanban] delivery: result post failed for card ${cardId} (task '${task.name}') on '${codeName}': ${outcome.error} — will retry`);
    return 'retry';
  }
  markScheduledCardDeliveryComplete(cardId);
  log(`[scheduled-kanban] Delivered result from card ${cardId} (task '${task.name}') for '${codeName}'`);
  return 'delivered';
}

// ENG-5600: happy-path close. Given a fresh board snapshot, close the per-card
// run for any tracked scheduled-task card the session has finished, and drop
// the map entry — completing the todo → in_progress → done lifecycle without
// leaking an open run. A card still todo/in_progress is left open; a card
// absent from this snapshot is left for the server-side reaper rather than
// guessed (it may just be filtered out of the board's done window). Idempotent:
// only acts on cards we're tracking, and deletes the entry on close so a
// repeated snapshot is a no-op.
export async function reconcileScheduledRuns(codeName: string, agentId: string, board: BoardItem[]): Promise<void> {
  const m = scheduledRunsByCode.get(codeName);
  if (!m || m.size === 0) return;
  const byId = new Map(board.map((i) => [i.id, i]));
  for (const [cardId, runId] of [...m.entries()]) {
    const card = byId.get(cardId);
    if (!card) continue; // not in snapshot — leave for the reaper, don't guess
    if (card.status === 'done') {
      // ENG-5601: deliver the agent's result, THEN close the run. This is the
      // PRIMARY delivery path — the agent self-completes the card so realtime
      // onCompletion is suppressed by isSelfCompletion and never fires here.
      // Critically, only drop the run-map entry once delivery has RESOLVED
      // ('delivered'/'terminal'). On a transient failure ('retry') or while
      // another path's claim is still in flight ('in_flight') the entry stays so
      // a later poll retries — the map IS the only retry handle, so deleting it
      // before a resolved delivery could silently drop the result.
      const result = await deliverScheduledCardResult(codeName, agentId, cardId, 'self');
      if (result === 'retry' || result === 'in_flight') continue;
      m.delete(cardId);
      void finishRun(runId, 'completed', { outcomeMessage: 'scheduled-task card done' });
    } else if (card.status === 'failed') {
      m.delete(cardId);
      void finishRun(runId, 'failed', { outcomeMessage: 'scheduled-task card failed' });
    }
  }
  if (m.size === 0) scheduledRunsByCode.delete(codeName);
}

// Materialise a due scheduled task as a TODO card bound to a per-card run, then
// nudge the live session to work it. Returns true when the card was created
// AND the nudge was delivered; false otherwise so the caller can fall back to
// the `claude -p` oneshot (keeps the cutover reversible and never drops a
// firing). Assumes the caller has already confirmed the session is healthy.
// ENG-6084: delivery-contract footer appended to every in-session
// scheduled-task card description. Lives at result-writing time (the agent
// re-reads the card to write its result) rather than only in the ephemeral
// task-start nudge — the gap that let conditional tasks keep delivering
// "nothing urgent" digests after PR #1829's nudge-only teaching. Exported for
// the kanban-hybrid tests.
export const SCHEDULED_CARD_DELIVERY_CONTRACT =
  `\n\n---\n[delivery contract — system]\n` +
  `The result you write on this card IS the message that will be delivered to the user. ` +
  `Delivery happens automatically from the card — never send, post, or message it yourself.\n` +
  `If — and ONLY if — the task above contains explicit opt-out wording the user typed ` +
  `("DO NOT notify me unless …", "only if X", "stay silent unless …") and that condition is ` +
  `NOT met this run, call kanban_done with suppress_delivery: true — your result is still ` +
  `recorded on the card but will NOT be messaged to the user. That is how you honor the ` +
  `user's do-not-notify instruction; a "nothing urgent" / "all quiet" result WITHOUT the flag ` +
  `would still be DELIVERED as a message. (Legacy fallback: writing exactly ` +
  `${SUPPRESS_SENTINEL} alone as the result also suppresses.) If the task asks for a ` +
  `digest/report WITHOUT opt-out wording, a zero-item report is a valid deliverable — deliver it.`;

// ENG-6546 (step 2): morning-plan side effect re-homed in-session. The oneshot
// path used to print a plan that the manager parsed into `kanban_add` calls
// (processClaudeTaskResult PLAN branch). In-session the agent owns that mutation
// directly via its kanban_* MCP tools, so this footer instructs it to CREATE the
// cards itself rather than writing the plan as free text — the manager no longer
// parses plan items (so there is no non-idempotent double-create on retry).
export const SCHEDULED_CARD_PLAN_INSTRUCTION =
  `\n\n---\n[planning task — system]\n` +
  `This is a board-PLANNING task. Review your current board with kanban_list, then create ` +
  `each item of today's plan as its OWN card using kanban_add (status: todo, with a sensible ` +
  `priority and estimate). Do NOT just write the plan as text — the cards you create with ` +
  `kanban_add ARE the deliverable; nothing parses a plan out of this card's result. When done, ` +
  `write a brief summary of the plan you created as this card's result and mark it done.`;

export async function routeScheduledTaskViaKanban(
  codeName: string,
  agentId: string,
  task: SchedulerTaskState,
  prompt: string,
): Promise<boolean> {
  // ENG-6546 (step 3): re-home the prior-runs + [NOW] context onto the card.
  // Previously only the oneshot path wrapped the prompt (wrapScheduledTaskPrompt),
  // so in-session recurring "report what's new" tasks re-reported unchanged items
  // and non-UTC tasks anchored dates to UTC. We DON'T apply the full wrapper here:
  // its EXECUTION-MODE preamble ("no human present", "recipient sees ONLY your
  // final text", "don't expose kanban status") describes the oneshot and
  // contradicts the in-session card model. buildScheduledTaskContextBlocks returns
  // only the model-agnostic halves — the [NOW] date anchor and the prior-runs
  // dedup block — which apply equally in-session. (The `<no-delivery/>` contract
  // is still re-homed separately via the delivery-contract footer below.)
  const priorRuns = await fetchPriorScheduledRuns(agentId, task.taskId);
  const contextBlocks = buildScheduledTaskContextBlocks({ priorRuns, timezone: task.timezone });
  //
  // Open the run and materialise the card together. The card carries the context
  // blocks + task prompt as its description (the in-session work spec) and starts
  // in 'todo' so the session promotes it itself.
  //
  // ENG-6084: the description also carries the delivery contract as a footer.
  // Teaching the <no-delivery/> opt-out ONLY in the nudge (PR #1829) proved
  // ineffective in practice: the nudge is one ephemeral turn at task-start,
  // but the agent re-reads the CARD when writing its result — and the card was
  // the raw task prompt with no contract. Scout's hourly urgent-email check
  // kept delivering "nothing urgent" digests for 10+ runs on the fixed CLI
  // because nothing at result-writing time said the card result IS the
  // outbound message. The footer puts the contract where the decision happens.
  // ENG-6546: morning-plan carries the plan-creation instruction (the agent
  // creates cards via kanban_add) instead of the generic delivery contract —
  // its "deliverable" is the cards it creates, not a parsed/delivered message.
  const cardDescription = contextBlocks + (PLAN_TEMPLATES.has(task.templateId)
    ? prompt + SCHEDULED_CARD_PLAN_INSTRUCTION
    : prompt + SCHEDULED_CARD_DELIVERY_CONTRACT);
  const { run_id, kanban_item_id } = await startRun({
    agent_id: agentId,
    source_type: 'scheduled_task',
    source_ref: task.taskId,
    metadata: { template_id: task.templateId, name: task.name, via: 'kanban-inject' },
    materialize_kanban: {
      title: task.name,
      description: cardDescription,
      priority: 2,
      initial_status: 'todo',
      ...deriveScheduledTaskNotify(task),
    },
  });

  if (!run_id || !kanban_item_id) {
    log(`[scheduled-kanban] materialise incomplete for task '${task.name}' on '${codeName}' (run_id=${run_id ? 'ok' : 'null'}, card=${kanban_item_id ? 'ok' : 'null'}) — falling back to claude -p`);
    // Close a half-opened run + card so the fallback oneshot doesn't leave a
    // phantom open run behind it.
    if (run_id) void finishRun(run_id, 'failed', { outcomeMessage: 'kanban materialise incomplete', completeKanbanItemId: kanban_item_id });
    return false;
  }

  // Targeted, per-card nudge. Plain text (not a slash command) so it lands as a
  // user turn via tmux send-keys; the run marker is appended so ENG-5566's
  // per-turn parser attributes this card's turns to this run. Title is JSON.stringify'd because
  // it's user-authored and could otherwise break the injected prompt shape
  // (same hardening as the realtime kanban-work trigger).
  // ENG-5601: the agent records its result on the card and marks it done, but
  // must NOT send/post the result itself — the manager delivers from the done
  // card (deliveryMode/channel/to honored centrally). Without this instruction
  // the card description (the raw task prompt) may tell the agent to "send me
  // X", which would double up with the manager's delivery.
  // ENG-4463 / ENG-4480 no-delivery contract, re-homed onto the in-session
  // route. The oneshot path (executeAndProcessClaudeTask) teaches this via
  // wrapScheduledTaskPrompt's preamble, but the in-session agent never sees
  // that preamble — the card description is the raw task prompt. Without this
  // clause a conditional task ("DO NOT notify me unless urgent") always wrote a
  // deliverable result onto the card, so the manager's classifyOutput gate had
  // nothing to suppress and the user got pinged every run (observed: Scout's
  // hourly urgent-email check messaging even when nothing was urgent). The gate
  // honours SUPPRESS_SENTINEL on the card result (deliverScheduledCardResult →
  // processClaudeTaskResult → classifyOutput); this clause is the missing half
  // that teaches the agent to emit it.
  // ENG-6107: conditional-policy tasks get a DIRECTIVE teaching — the manager
  // already decided this task is do-not-notify-by-default, so the agent makes
  // no judgment about wording; quiet runs are silent unless it ASSERTS
  // delivery. Other tasks keep the generic judgment-based teaching.
  const suppressionTeaching =
    (task.deliveryPolicy ?? 'always') === 'conditional'
      ? `THIS task is CONDITIONAL: quiet runs stay SILENT automatically. Write your findings on the ` +
        `card and mark it done — if nothing met the user's criteria, NOTHING will be sent (no flag ` +
        `needed). If something DID meet the criteria and the user must be told, call kanban_done ` +
        `with suppress_delivery: false AND delivery_reason naming the concrete trigger that fired ` +
        `(e.g. "urgent email from the CEO", "CI failing on main"); a vacuous reason like "status ` +
        `check" or "all clear" is rejected and stays silent. That explicit, justified assert is the ` +
        `ONLY way this result reaches the user.\n`
      : `If — and ONLY if — the task description contains explicit opt-out wording the user typed ` +
        `(e.g. "DO NOT notify me unless urgent", "only if X", "stay silent unless …") and that condition ` +
        `is NOT met this run, call kanban_done with suppress_delivery: true — your result stays on the ` +
        `card but will NOT be messaged to the user. That is how you honor the user's do-not-notify ` +
        `instruction: a "nothing urgent" result without the flag would still be DELIVERED as a message. ` +
        `A report with zero items is only a valid deliverable when the task asks for a digest WITHOUT ` +
        `opt-out wording — when in doubt, deliver.\n`;
  const nudge =
    `You have a new scheduled task on your kanban board: id=${kanban_item_id} ` +
    `title=${JSON.stringify(task.name)}. Call kanban_move("${kanban_item_id}", "in_progress"), ` +
    `do the work described on the card, then write the finished result onto the card and mark it done. ` +
    `Do NOT send, post, or message the result to anyone yourself — the result you write on the card ` +
    `IS the message that will be delivered to the user; delivery happens automatically from the card.\n` +
    suppressionTeaching +
    formatRunMarker(run_id);

  // Use the status-returning variant: a bare `false` from injectMessage is
  // ambiguous — tmux send-keys returns it even after the keystroke landed. If
  // we fell back to the oneshot on that, we'd BOTH nudge the live session and
  // spawn claude -p, double-executing the task. So only the "neither delivered
  // nor sent" case is a real failure that warrants fallback.
  // A rejection here (vs the normal {delivered,fallbackUsed} resolve) would
  // exit after the run + card were materialised, and the caller would fall back
  // to claude -p — orphaning the card/run and double-executing the firing.
  // Catch it: close the card + run and return false (fall back cleanly).
  let injectStatus: import('../../persistent-session.js').InjectResult;
  try {
    injectStatus = await injectMessageWithStatus(codeName, 'task', nudge, { task_name: 'scheduled-task' }, log);
  } catch (err) {
    log(`[scheduled-kanban] nudge injection threw for task '${task.name}' on '${codeName}': ${(err as Error).message} — closing card + falling back to claude -p`);
    void finishRun(run_id, 'failed', { outcomeMessage: 'scheduled-task nudge delivery threw', completeKanbanItemId: kanban_item_id });
    return false;
  }
  const { delivered, fallbackUsed } = injectStatus;
  if (!delivered && !fallbackUsed) {
    log(`[scheduled-kanban] nudge delivery failed for task '${task.name}' on '${codeName}' — closing card + falling back to claude -p`);
    // Close the card + run so the fallback oneshot doesn't double up on a card
    // the session never picked up.
    void finishRun(run_id, 'failed', { outcomeMessage: 'scheduled-task nudge delivery failed', completeKanbanItemId: kanban_item_id });
    return false;
  }
  if (!delivered && fallbackUsed) {
    // Sent via tmux send-keys (submission unverified). Treat as committed so we
    // don't also spawn the oneshot — preferable to the double-billing this epic
    // exists to eliminate.
    // KNOWN GAP (ENG-6351): if the keystroke didn't actually submit, this card
    // is orphaned in 'todo' with NO automated recovery — the S2
    // kanban-stale-item-reaper and the 30-min stale auto-fail are both
    // in_progress-only, and the hybrid re-nudge excludes scheduled_task cards
    // (isHybridActionable), so the task silently never runs. The durable fix is
    // confirmed delivery (ENG-6346 / ENG-5927). (Corrected here: this was
    // previously, and wrongly, documented as "recovery is the S2 reaper's job".)
    log(`[scheduled-kanban] nudge sent via send-keys (unverified) for task '${task.name}' on '${codeName}' — proceeding without oneshot fallback`);
  }

  trackScheduledRun(codeName, kanban_item_id, run_id);
  log(`[scheduled-kanban] Routed task '${task.name}' for '${codeName}' via todo card ${kanban_item_id} run_id=${run_id}`);
  return true;
}

/**
 * ENG-6849: fire a scheduled task through the kanban todo-card + in-session
 * nudge path - the SOLE scheduled-task path now that the `claude -p` oneshot is
 * retired (ENG-5602 made it terminal; ENG-6849 deleted the fallback). Used by
 * BOTH scheduler callers (the persistent-session driver and non-persistent
 * `syncAndCheckClaudeScheduler`) for every template except kanban-work, which
 * is skipped upstream.
 *
 * Always advances the schedule (`markTaskFired`) and disables a one-off (`at`)
 * task server-side, regardless of routing outcome:
 *   - routed === true  → the card + nudge carry the firing in-session.
 *   - routed === false → `routeScheduledTaskViaKanban` has already closed the
 *     card + run as failed before returning, and the S2 reaper (ENG-5600)
 *     backstops any todo a send-keys nudge left unpicked. There is no oneshot
 *     fallback to rescue it, so we record 'error' and move on.
 *
 * The caller owns the in-flight / concurrency guard release and loop control
 * (break vs continue); this helper returns the routing result purely for the
 * caller's logging if wanted.
 */
export async function fireScheduledTaskViaKanban(
  codeName: string,
  agentId: string,
  task: SchedulerTaskState,
  prompt: string,
): Promise<boolean> {
  let routed = false;
  try {
    routed = await routeScheduledTaskViaKanban(codeName, agentId, task, prompt);
  } catch (err) {
    log(`[scheduled-kanban] route threw for '${task.name}' on '${codeName}': ${(err as Error).message}`);
    routed = false;
  }
  // Guard the state write so a disk error can't leak the caller's in-flight /
  // concurrency guard (which would wedge the task until restart). 'error' on a
  // failed route so the firing isn't recorded as a clean 'ok'.
  try {
    const updated = markTaskFired(codeName, task.taskId, routed ? 'ok' : 'error');
    claudeSchedulerStates.set(codeName, updated);
  } catch (err) {
    log(`[scheduled-kanban] markTaskFired failed for '${task.name}' on '${codeName}': ${(err as Error).message}`);
  }
  if (task.scheduleKind === 'at') {
    // One-off: disable server-side regardless of outcome, matching the oneshot
    // path's unconditional disable — a fired one-off doesn't linger. AWAIT it
    // (CodeRabbit PR #1664): this helper is the terminal routed path, so if the
    // manager exits after markTaskFired() advanced local state but before the
    // disable POST lands, the still-enabled server row would be re-imported and
    // the 'at' task fired again on next boot. Awaiting holds the firing loop
    // until the POST settles, closing that window. A failure is logged
    // (best-effort) rather than thrown so a transient API error can't wedge the
    // loop or escape as an unhandled rejection.
    try {
      await api.post('/host/schedules/disable', { agent_id: agentId, task_id: task.taskId });
    } catch (err) {
      log(`[scheduled-kanban] Failed to disable one-off task '${task.name}': ${(err as Error).message}`);
    }
  }
  return routed;
}


// ENG-6430: processClaudeTaskResult used to return void and swallow every
// posting/delivery failure, so callers recorded a lost result as success —
// deliverScheduledCardResult closed the retry handle and executeAndProcessClaudeTask
// marked the run completed/ok. It now returns a structured outcome: `ok` means
// the result was posted/delivered (or intentionally suppressed); `!ok` means a
// /host post or outbound delivery threw and the caller MUST NOT close the retry
// handle or record success.
export type ScheduledResultOutcome =
  | { ok: true }
  | { ok: false; error: string };

export async function processClaudeTaskResult(
  codeName: string,
  agentId: string,
  templateId: string,
  rawOutput: string,
  // ENG-6546: `skipBoardSideEffects` tells the in-session delivery path to NOT
  // parse board-mutating templates (morning-plan → /host/kanban add; the legacy
  // kanban-work parse) from the card result — the in-session agent owns those
  // mutations via its kanban_* MCP tools, so a manager parse would double-create.
  // The idempotent status posts (standup / task-update → /host/agent-status) are
  // NOT board side effects and still run. The oneshot path leaves it unset
  // (false) so its stdout is parsed as before.
  // ENG-6803 / ENG-6849: `deliveryPolicy` is no longer passed by any production
  // caller - the `claude -p` oneshot that passed it was retired in ENG-6849. The
  // in-session kanban caller gates delivery_policy upstream in
  // deliverScheduledCardResult (via the structured suppress_delivery flag) and
  // leaves this unset, so the conditional/never gate below stays inert in
  // production. Retained as a defensive gate and exercised directly by unit tests.
  delivery?: { mode: string; channel: string | null; to: unknown | null; taskId?: string; skipBoardSideEffects?: boolean; deliveryPolicy?: string | null },
): Promise<ScheduledResultOutcome> {
  const skipBoardSideEffects = delivery?.skipBoardSideEffects === true;
  try {
    // ENG-6803 / ENG-6849: legacy oneshot-path delivery_policy enforcement. The
    // in-session kanban route enforces delivery_policy upstream
    // (deliverScheduledCardResult) and does NOT pass `deliveryPolicy`, and the
    // `claude -p` oneshot that did pass it is retired (ENG-6849), so in
    // production `policy` is always null and this block is inert. Kept as a
    // defensive gate (and unit-tested directly): historically a `conditional`/
    // `never` task on the oneshot path delivered every run because it only
    // honoured the `<no-delivery/>` opt-OUT sentinel (Vera leaked 24x/day).
    const policy = delivery?.deliveryPolicy ?? null;
    if (policy === 'never') {
      const trimmed = (rawOutput ?? '').trim();
      log(`[claude-scheduler] Suppressed by delivery_policy=never for '${codeName}' (template=${templateId}, task=${delivery?.taskId ?? 'n/a'}) — output_len=${trimmed.length}; recorded only, not delivered`);
      if (delivery?.mode === 'announce' && delivery.to) {
        await reportDeliveryStatus(agentId, delivery.taskId, { status: 'skipped', error_code: 'SUPPRESSED_BY_POLICY' });
      }
      return { ok: true };
    }
    if (policy === 'conditional') {
      const assertion = parseDeliverAssertion(rawOutput);
      if (!assertion.deliver) {
        // Suppressed by default: no `<deliver: reason>` marker, or the reason
        // was vacuous ("no change" / "all clear" / "status update"). Hash-only
        // logging — suppressed output can carry recipient text / PII.
        const trimmed = (rawOutput ?? '').trim();
        const outputHash = trimmed.length === 0 ? 'empty' : createHash('sha256').update(trimmed).digest('hex').slice(0, 12);
        log(`[claude-scheduler] Suppressed by delivery_policy=conditional for '${codeName}' (template=${templateId}, task=${delivery?.taskId ?? 'n/a'}) — ${assertion.vacuous ? 'deliver marker had a vacuous reason' : 'no deliver marker'}; output_len=${trimmed.length} output_hash=${outputHash}`);
        if (delivery?.mode === 'announce' && delivery.to) {
          await reportDeliveryStatus(agentId, delivery.taskId, { status: 'skipped', error_code: 'SUPPRESSED_BY_POLICY' });
        }
        return { ok: true };
      }
      // Concrete trigger named — deliver the marker-stripped output. Hash the
      // reason rather than logging it raw (it can name sensitive context).
      const reasonHash = createHash('sha256').update(assertion.reason ?? '').digest('hex').slice(0, 12);
      log(`[claude-scheduler] Delivering conditional run for '${codeName}' (template=${templateId}, task=${delivery?.taskId ?? 'n/a'}) — agent asserted a concrete trigger (reason_len=${(assertion.reason ?? '').length} reason_hash=${reasonHash})`);
      rawOutput = assertion.deliverable;
    }
    // ENG-4463 / ENG-4480: classify the output before ANYTHING else —
    // suppress / strip / deliver. Conditional tasks ("DO NOT notify me
    // unless X") return `<no-delivery/>` alone to signal "nothing worth
    // delivering this run". Some agents mix the sentinel into otherwise
    // real content (observed: "Nothing urgent ...\n\n<no-delivery/>\n\n—
    // scheduled by ..."); rather than ship the token to the recipient,
    // we strip every sentinel occurrence and deliver the cleaned text.
    // This must short-circuit template handlers too — otherwise a standup
    // scheduled task could still write "current_tasks: <no-delivery/>"
    // into /host/agent-status before we decide to skip the outbound.
    const classification = classifyOutput(rawOutput);
    if (classification.action === 'suppress') {
      // Hash + length only — suppressed output can contain recipient text
      // or PII the agent pulled from Linear/Gmail/etc. and `manager.log`
      // is persisted to disk. redactForDiskLog catches token-shaped
      // secrets but not message bodies, so we never log them raw here.
      const trimmed = (rawOutput ?? '').trim();
      const outputHash = trimmed.length === 0
        ? 'empty'
        : createHash('sha256').update(trimmed).digest('hex').slice(0, 12);
      log(`[claude-scheduler] Suppressing delivery for '${codeName}' (template=${templateId}, task=${delivery?.taskId ?? 'n/a'}) — output_len=${trimmed.length} output_hash=${outputHash}`);
      if (classification.suppressedNotes) {
        const notesHash = createHash('sha256').update(classification.suppressedNotes).digest('hex').slice(0, 12);
        log(`[claude-scheduler] Suppressed notes for '${codeName}' (task=${delivery?.taskId ?? 'n/a'}) — notes_len=${classification.suppressedNotes.length} notes_hash=${notesHash}`);
      }
      if (delivery?.mode === 'announce' && delivery.to) {
        await reportDeliveryStatus(agentId, delivery.taskId, {
          status: 'skipped',
          error_code: 'NO_CONTENT',
        });
      }
      return { ok: true };
    }

    // Either 'deliver' (no sentinel present) or 'strip' (sentinel removed,
    // clean deliverable remains). Template handlers and the outbound both
    // use the classified deliverable, never the raw output.
    const output = classification.deliverable;
    if (classification.action === 'strip') {
      log(`[claude-scheduler] Stripped '<no-delivery/>' sentinel from '${codeName}' output (template=${templateId}, task=${delivery?.taskId ?? 'n/a'}) — agent mixed it with real content; delivering the rest.`);
    }

    if (STANDUP_TEMPLATES.has(templateId)) {
      const standup = parseStandupSummary(output);
      await api.post('/host/agent-status', {
        agent_code_name: codeName,
        standup,
        current_status: 'idle',
      });
      log(`[claude-scheduler] Standup posted for '${codeName}'`);
    } else if (TASK_UPDATE_TEMPLATES.has(templateId)) {
      await api.post('/host/agent-status', {
        agent_code_name: codeName,
        current_tasks: output.slice(0, 2000),
      });
      log(`[claude-scheduler] Task update posted for '${codeName}'`);
    } else if (PLAN_TEMPLATES.has(templateId) && !skipBoardSideEffects) {
      // Oneshot path only (skipBoardSideEffects=false): parse the agent's printed
      // plan and create the cards manager-side. On the in-session path the agent
      // creates them itself via kanban_add, so this branch is skipped to avoid a
      // non-idempotent double-create (ENG-6546).
      const planItems = parsePlanItems(output);
      if (planItems.length > 0) {
        await api.post('/host/kanban', {
          agent_id: agentId,
          add: planItems,
        });
        log(`[claude-scheduler] Plan items posted for '${codeName}' (${planItems.length} items)`);
      }
    } else if (KANBAN_WORK_TEMPLATES.has(templateId) && !skipBoardSideEffects) {
      const kanbanUpdates = parseKanbanUpdates(output);
      if (kanbanUpdates.length > 0) {
        await api.post('/host/kanban', {
          agent_id: agentId,
          update: kanbanUpdates,
        });
        log(`[claude-scheduler] Kanban updates posted for '${codeName}' (${kanbanUpdates.length} updates)`);
      }
    }

    // ENG-4422 §5–§6: deliver output via the configured target.
    // Target is either a legacy-shape `channel:<id>` / `chat:<id>` string
    // (OpenClaw agents still produce this form) OR a JSONB DeliveryTarget
    // object (Claude Code agents post-migration). Suppressed output was
    // already short-circuited above, so if we reach here the output is
    // deliverable.
    if (delivery?.mode === 'announce' && delivery.to) {
      await deliverScheduledTaskOutput(
        codeName,
        agentId,
        delivery.to,
        output.slice(0, 4000),
        delivery.taskId,
      );
    }
    return { ok: true };
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err);
    log(`[claude-scheduler] Failed to post result for '${codeName}': ${errMsg}`);
    return { ok: false, error: errMsg };
  }
}
